import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useForm } from "react-hook-form";

const FilterComponent = forwardRef((props, ref) => {
  const {
    handleDuration,
    allCPs,
    isServiceRevenue,
    userSessions,
    networkData,
  } = props;

  const {
    register: register1,
    handleSubmit: handleSubmit1,
    setValue,
    formState: {
      errors: errors1,
      isSubmitting: isSubmitting1,
      isValid: isValid1,
    },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      operator: "",
      service_id: "",
      cp_id: "0|",
      fro: new Date().toISOString().substr(0, 10),
      to: new Date().toISOString().substr(0, 10),
    },
  });

  // Service Revenue hooks
  const [serviceSearch, setServiceSearch] = useState("");
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const serviceDropdownRef = useRef(null);
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [filteredServices, setFilteredServices] = useState([]);

  // Expose reset function to parent
  useImperativeHandle(ref, () => ({
    resetService: () => {
      setSelectedService(null);
      setServiceSearch("");
      setValue("service_id", "");
    },
  }));

  // Close dropdown when clicking outside (for Service Revenue)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target)
      ) {
        setShowServiceDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter services for Service Revenue
  useEffect(() => {
    let services =
      userSessions?.filter(
        (service) =>
          service.serviceName
            .toLowerCase()
            .includes(serviceSearch.toLowerCase()) ||
          service.serviceId.toLowerCase().includes(serviceSearch.toLowerCase()),
      ) || [];

    if (selectedNetwork) {
      services = services.filter(
        (service) => service.network_id == selectedNetwork,
      );
    }
    setFilteredServices(services);
  }, [userSessions, serviceSearch, selectedNetwork]);

  const handleNetworkChange = (e) => {
    const selectedNetworkId = e.target.value;
    setSelectedNetwork(selectedNetworkId);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setServiceSearch(service.serviceName);
    setValue("service_id", service.serviceId);
    setShowServiceDropdown(false);
  };

  const handleServiceInputChange = (e) => {
    setServiceSearch(e.target.value);
    setShowServiceDropdown(true);
    if (e.target.value === "") {
      setSelectedService(null);
      setValue("service_id", "");
    }
  };

  if (isServiceRevenue) {
    return (
      <div className="row mb-10 mt-40">
        <form onSubmit={handleSubmit1(handleDuration)} className="row">
          <div className="col-lg-12">
            <div className="col-sm-3 pr-0">
              <select
                className="filter_text"
                {...register1("operator", {
                  required: "Please select an operator",
                })}
                style={{ textTransform: "capitalize" }}
                onChange={handleNetworkChange}
              >
                <option value="">-Select Operator-</option>
                {networkData &&
                  networkData.length > 0 &&
                  networkData.map((network) => {
                    return (
                      <option key={network.id} value={network.id}>
                        {network.name}
                      </option>
                    );
                  })}
              </select>
              {errors1.operator && (
                <small className="text-danger">
                  {errors1.operator.message}
                </small>
              )}
            </div>

            <div className="col-sm-3 pr-0" ref={serviceDropdownRef}>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  className="filter_text"
                  placeholder="Search Service..."
                  value={serviceSearch}
                  onChange={handleServiceInputChange}
                  onFocus={() => setShowServiceDropdown(true)}
                  style={{ textTransform: "capitalize" }}
                  autoComplete="off"
                />
                <input
                  type="hidden"
                  {...register1("service_id", {
                    required: "Please select a service",
                  })}
                />

                {showServiceDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                      backgroundColor: "white",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      zIndex: 1000,
                      marginTop: "2px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {filteredServices.length > 0 ? (
                      filteredServices.map((service) => (
                        <div
                          key={service.serviceId}
                          onClick={() => handleServiceSelect(service)}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            borderBottom: "1px solid #f0f0f0",
                            textTransform: "capitalize",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f5f5f5")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "white")
                          }
                        >
                          {service.serviceName}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "8px 12px", color: "#999" }}>
                        No services found
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors1.service_id && (
                <small className="text-danger">
                  {errors1.service_id.message}
                </small>
              )}
            </div>

            <div className="col-sm-6 pl-5 pr-15 mt-xs-5 pl-xs-20">
              <div className="col-sm-4 col-4 pl-5 pr-0">
                <input
                  type="date"
                  className="filter_text"
                  placeholder="From Date"
                  {...register1("fro", { required: "From date is required" })}
                  max={new Date().toISOString().substr(0, 10)}
                />
                {errors1.fro && (
                  <small className="text-danger">{errors1.fro.message}</small>
                )}
              </div>
              <div className="col-sm-4 col-4 pl-5 pr-0">
                <input
                  type="date"
                  className="filter_text"
                  placeholder="To Date"
                  {...register1("to", { required: "To date is required" })}
                  max={new Date().toISOString().substr(0, 10)}
                />
                {errors1.to && (
                  <small className="text-danger">{errors1.to.message}</small>
                )}
              </div>
              <div className="col-sm-4 col-4 p-0">
                <button
                  className="btn_1 btn-primary w-100"
                  type="submit"
                  disabled={isSubmitting1}
                  style={{
                    opacity: isSubmitting1 ? "0.5" : 1,
                    minHeight: "38px",
                  }}
                >
                  {isSubmitting1 ? (
                    <>
                      <i className="fa fa-spinner fa-spin mr-2"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-search mr-2"></i>
                      Filter
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

  // Regular CP Revenue filter
  return (
    <div className="row mb-10 mt-40">
      <form onSubmit={handleSubmit1(handleDuration)} className="row">
        <div className="col-lg-12">
          <div className="col-sm-4 pr-0">
            <select
              className="filter_text"
              {...register1("cp_id", { required: "Please select a CP" })}
              style={{ textTransform: "capitalize" }}
            >
              <option value="0|">-Select CP-</option>
              {allCPs &&
                allCPs.length > 0 &&
                allCPs.map((cp) => {
                  return (
                    <option key={cp.id} value={`${cp.id}|${cp.name}`}>
                      {cp.name}
                    </option>
                  );
                })}
            </select>
            {errors1.cp_id && (
              <small className="text-danger">{errors1.cp_id.message}</small>
            )}
          </div>

          <div className="col-sm-8 pl-5 pr-15 mt-xs-5 pl-xs-20">
            <div className="col-sm-4 col-4 pl-5 pr-0">
              <input
                type="date"
                className="filter_text"
                placeholder="From Date"
                {...register1("fro", { required: "From date is required" })}
                max={new Date().toISOString().substr(0, 10)}
              />
              {errors1.fro && (
                <small className="text-danger">{errors1.fro.message}</small>
              )}
            </div>
            <div className="col-sm-4 col-4 pl-5 pr-0">
              <input
                type="date"
                className="filter_text"
                placeholder="To Date"
                {...register1("to", { required: "To date is required" })}
                max={new Date().toISOString().substr(0, 10)}
              />
              {errors1.to && (
                <small className="text-danger">{errors1.to.message}</small>
              )}
            </div>
            <div className="col-sm-4 col-4 p-0">
              <button
                className="btn_1 btn-primary w-100"
                type="submit"
                disabled={isSubmitting1}
                style={{
                  opacity: isSubmitting1 ? "0.5" : 1,
                  minHeight: "38px",
                }}
              >
                {isSubmitting1 ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-2"></i>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fa fa-search mr-2"></i>
                    Filter
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
});

export default FilterComponent;
